import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PiArrowsClockwiseLight, PiChatCircleTextLight, PiProhibitLight, PiXCircleLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { Well } from '../shared/brand/Field';
import { formatAbsoluteTime, formatRelativeTime } from '../shared/feed/feedTime';
import {
  DECLINE_REASONS,
  DeclineReason,
  Proposal,
  ProposalHistoryEntry,
  declineProposal,
  errorMessage,
  requestChanges,
  reviseProposal,
  withdrawProposal,
} from '../../services/api/marketplaceService';
import { amountLabel, fmtMoney } from './marketplaceUi';

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;
const MAX_NOTE = 2000; // mirrors the backend

/** A proposal can still be negotiated (and accepted) while proposed or countered. */
export const isLive = (p: Proposal): boolean => p.status === 'proposed' || p.status === 'countered';

const declineReasonLabel = (t: TFunction, reason: DeclineReason): string =>
  ({
    price: t('The price'),
    plan: t('The plan'),
    chose_other: t('I chose another lawyer'),
    no_longer_needed: t('I no longer need help'),
    other: t('Something else'),
  })[reason];

const entryTitle = (t: TFunction, e: ProposalHistoryEntry, currency: string, viewer: 'client' | 'lawyer'): string => {
  const who = e.by === viewer ? t('You') : e.by === 'client' ? t('The client') : t('The lawyer');
  switch (e.action) {
    case 'requested_changes':
      return e.amount
        ? t('{{who}} asked for changes (suggested {{price}})', { who, price: fmtMoney(e.amount, currency) })
        : t('{{who}} asked for changes', { who });
    case 'revised':
      return e.amount
        ? t('{{who}} revised the proposal ({{price}})', { who, price: fmtMoney(e.amount, currency) })
        : t('{{who}} revised the proposal', { who });
    case 'declined':
      return e.reason
        ? t('{{who}} declined: {{reason}}', { who, reason: declineReasonLabel(t, e.reason) })
        : t('{{who}} declined', { who });
    case 'withdrawn':
      return t('{{who}} withdrew the proposal', { who });
    default:
      return who;
  }
};

/** The back-and-forth on one proposal, oldest first. */
export const ProposalHistory: React.FC<{ proposal: Proposal; viewer: 'client' | 'lawyer' }> = ({ proposal, viewer }) => {
  const { t } = useTranslation();
  const entries = proposal.history ?? [];
  if (!entries.length) return null;
  return (
    <ul className="space-y-1.5" data-testid="marketplace-proposal-history">
      {entries.map((e, i) => (
        <li key={`${e.at}-${i}`}>
          <Well className="px-3 py-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[12px] text-fg-strong">{entryTitle(t, e, proposal.currency, viewer)}</span>
              <span className="shrink-0 text-[11px] text-fg-muted" title={formatAbsoluteTime(e.at)}>
                {formatRelativeTime(e.at, t)}
              </span>
            </div>
            {e.note && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-fg-body">{e.note}</p>}
          </Well>
        </li>
      ))}
    </ul>
  );
};

const useToast = () => {
  const dispatch = useAppDispatch();
  return (modalType: 'success' | 'error', message: string) =>
    dispatch(showFeedbackModal({ showModal: true, modalType, duration: 4000, message }));
};

/**
 * Client: "Ask for changes" or "Decline" on a live proposal. Before hiring the
 * lawyer sees only the triage, so the form asks the client to leave personal
 * details out.
 */
export const ClientProposalActions: React.FC<{
  proposal: Proposal;
  disabled?: boolean;
  onChanged: (p: Proposal) => void;
}> = ({ proposal, disabled, onChanged }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [mode, setMode] = useState<'changes' | 'decline' | null>(null);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<DeclineReason | ''>('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    setMode(null);
    setNote('');
    setAmount('');
    setReason('');
  };

  const run = async (fn: () => Promise<Proposal>, success: string, failure: string) => {
    setBusy(true);
    try {
      onChanged(await fn());
      toast('success', success);
      close();
    } catch (err) {
      toast('error', errorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const amountOk = !amount.trim() || AMOUNT_RE.test(amount.trim());
  const noteTooLong = note.length > MAX_NOTE;

  if (mode === null) {
    return (
      <div className="flex flex-wrap justify-end gap-2" data-testid="marketplace-client-negotiate">
        <Button variant="outline" size="small" onClick={() => setMode('changes')} disabled={disabled}
          icon={<PiChatCircleTextLight className="h-3.5 w-3.5" />}>
          {t('Ask for changes')}
        </Button>
        <Button variant="outline" size="small" onClick={() => setMode('decline')} disabled={disabled}
          icon={<PiXCircleLight className="h-3.5 w-3.5" />}>
          {t('Decline')}
        </Button>
      </div>
    );
  }

  return (
    <Well className="space-y-3 px-3 py-3" data-testid={`marketplace-${mode}-form`}>
      {mode === 'changes' ? (
        <>
          <Textarea id={`changes-note-${proposal.uuid}`} label={t('What would you like changed?')} rows={3}
            value={note} onChange={(e) => setNote(e.target.value)} required
            error={noteTooLong ? t('Max {{n}} characters', { n: MAX_NOTE }) : undefined} />
          <Input type="text" id={`changes-amount-${proposal.uuid}`} inputMode="decimal"
            label={t('Price you would accept ({{currency}}, optional)', { currency: proposal.currency })}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            error={amountOk ? undefined : t('Enter an amount like 1500.00')} />
          <p className="text-[11px] text-fg-muted">
            {t("The lawyer doesn't know who you are yet. Leave out names and other personal details.")}
          </p>
        </>
      ) : (
        <>
          <Dropdown id={`decline-reason-${proposal.uuid}`} label={t('Why? (optional)')}
            options={DECLINE_REASONS.map((r) => ({ value: r, label: declineReasonLabel(t, r) }))}
            value={reason} onChange={(v) => setReason(v as DeclineReason)} fullWidth />
          <Textarea id={`decline-note-${proposal.uuid}`} label={t('Note to the lawyer (optional)')} rows={2}
            value={note} onChange={(e) => setNote(e.target.value)}
            error={noteTooLong ? t('Max {{n}} characters', { n: MAX_NOTE }) : undefined} />
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="small" onClick={close} disabled={busy}>{t('Cancel')}</Button>
        {mode === 'changes' ? (
          <Button variant="primary" size="small" isLoading={busy}
            disabled={busy || !note.trim() || noteTooLong || !amountOk}
            onClick={() => run(() => requestChanges(proposal.uuid, {
              note: note.trim(), ...(amount.trim() ? { amount: amount.trim() } : {}),
            }), t('Sent to the lawyer'), t('Could not send your request'))}>
            {t('Send')}
          </Button>
        ) : (
          <Button variant="destructive" size="small" isLoading={busy} disabled={busy || noteTooLong}
            onClick={() => run(() => declineProposal(proposal.uuid, {
              ...(reason ? { reason } : {}), ...(note.trim() ? { note: note.trim() } : {}),
            }), t('Proposal declined'), t('Could not decline the proposal'))}>
            {t('Decline proposal')}
          </Button>
        )}
      </div>
    </Well>
  );
};

/** Lawyer: revise (new price / plan / terms) or withdraw a live proposal. */
export const LawyerProposalActions: React.FC<{ proposal: Proposal; onChanged: (p: Proposal) => void }> = ({
  proposal, onChanged,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [revising, setRevising] = useState(false);
  const [amount, setAmount] = useState(proposal.amount);
  const [plan, setPlan] = useState(proposal.plan_summary);
  const [terms, setTerms] = useState(proposal.terms ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const open = () => {
    setAmount(proposal.amount);
    setPlan(proposal.plan_summary);
    setTerms(proposal.terms ?? '');
    setNote('');
    setRevising(true);
  };

  const amountOk = AMOUNT_RE.test(amount.trim());
  const changed = Number(amount) !== Number(proposal.amount) || plan.trim() !== proposal.plan_summary
    || terms.trim() !== (proposal.terms ?? '');

  const send = async () => {
    setBusy(true);
    try {
      const body: { amount?: string; plan_summary?: string; terms?: string; note?: string } = {};
      if (Number(amount) !== Number(proposal.amount)) body.amount = amount.trim();
      if (plan.trim() !== proposal.plan_summary) body.plan_summary = plan.trim();
      if (terms.trim() !== (proposal.terms ?? '')) body.terms = terms.trim();
      if (note.trim()) body.note = note.trim();
      onChanged(await reviseProposal(proposal.uuid, body));
      toast('success', t('Revised proposal sent'));
      setRevising(false);
    } catch (err) {
      toast('error', errorMessage(err, t('Could not revise the proposal')));
    } finally {
      setBusy(false);
    }
  };

  const confirmWithdraw = () => dispatch(showModal({
    showModal: true,
    type: 'confirmation',
    title: t('Withdraw proposal'),
    message: t('Withdraw your proposal?'),
    subText: t('The client is told, and can no longer accept it.'),
    icon: <PiProhibitLight className="w-6 h-6 text-danger-600" />,
    variant: 'danger',
    confirmLabel: t('Withdraw'),
    onConfirm: async () => {
      try {
        onChanged(await withdrawProposal(proposal.uuid, {}));
        toast('success', t('Proposal withdrawn'));
      } catch (err) {
        toast('error', errorMessage(err, t('Could not withdraw the proposal')));
      }
    },
  }));

  if (!revising) {
    return (
      <div className="flex flex-wrap justify-end gap-2" data-testid="marketplace-lawyer-negotiate">
        <Button variant="outline" size="small" onClick={confirmWithdraw}>{t('Withdraw')}</Button>
        <Button variant={proposal.status === 'countered' ? 'primary' : 'outline'} size="small" onClick={open}
          icon={<PiArrowsClockwiseLight className="h-3.5 w-3.5" />}>
          {t('Revise proposal')}
        </Button>
      </div>
    );
  }

  return (
    <Well className="space-y-3 px-3 py-3" data-testid="marketplace-revise-form">
      <Input type="text" id="revise-amount" inputMode="decimal"
        label={`${amountLabel(t, proposal.fee_basis)} (${proposal.currency})`}
        value={amount} onChange={(e) => setAmount(e.target.value)}
        error={amountOk ? undefined : t('Enter an amount like 1500.00')} />
      <Textarea id="revise-plan" label={t('Plan summary')} value={plan} onChange={(e) => setPlan(e.target.value)} required />
      <Textarea id="revise-terms" label={t('Terms (optional)')} rows={2} value={terms}
        onChange={(e) => setTerms(e.target.value)} />
      <Textarea id="revise-note" label={t('Note to the client (optional)')} rows={2} value={note}
        onChange={(e) => setNote(e.target.value)} />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="small" onClick={() => setRevising(false)} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={send} isLoading={busy}
          disabled={busy || !amountOk || !plan.trim() || !changed}>
          {t('Send revision')}
        </Button>
      </div>
    </Well>
  );
};
